<template>
  <div class="amazon-shipment-page">
    <!-- 头部 -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <el-icon size="28" style="margin-right:8px;vertical-align:middle;color:#667eea;"><Van /></el-icon>
          亚马逊货件列表
        </h1>
        <p class="page-subtitle">查看您的亚马逊FBA货件，掌握货件状态和详情</p>
      </div>
      <div class="header-actions">
        <el-tooltip
          effect="dark"
          placement="top"
          content="从亚马逊卖家后台手动同步最新货件数据。页面数据每小时自动更新一次，如需查看最新货件信息可点击此按钮。"
        >
          <el-button type="warning" @click="syncAllData" :loading="syncLoading">
            <el-icon><RefreshRight /></el-icon>
            同步最新数据
          </el-button>
        </el-tooltip>
        <el-button type="primary" @click="refreshData" :loading="refreshLoading">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-bar">
      <div class="filter-group">
        <el-select
          v-model="selectedShopId"
          placeholder="选择店铺"
          style="width: 180px"
          @change="handleShopChange"
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.shop_name"
            :value="shop.id"
          />
          <el-option value="__refresh__" label="🔄 刷新店铺列表" />
        </el-select>
        <el-input
          v-model="searchForm.inbound_plan_id"
          placeholder="入库计划ID"
          clearable
          style="width: 220px"
          @keyup.enter="handleSearch"
        >
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <el-input
          v-model="searchForm.shipment_confirmation_id"
          placeholder="货件编号"
          clearable
          style="width: 180px"
          @keyup.enter="handleSearch"
        >
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <el-input
          v-model="searchForm.shipment_name"
          placeholder="货件名称"
          clearable
          style="width: 180px"
          @keyup.enter="handleSearch"
        >
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <el-input
          v-model="searchForm.amazon_reference_id"
          placeholder="亚马逊参考号"
          clearable
          style="width: 160px"
          @keyup.enter="handleSearch"
        >
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <el-select
          v-model="searchForm.destination_warehouse_id"
          placeholder="目标仓库"
          clearable
          filterable
          style="width: 140px"
        >
          <el-option
            v-for="wh in warehouses"
            :key="wh.warehouse_id"
            :label="wh.warehouse_id"
            :value="wh.warehouse_id"
          />
        </el-select>
        <el-select
          v-model="searchForm.status"
          placeholder="状态"
          clearable
          style="width: 130px"
        >
          <el-option label="全部" value="" />
          <el-option label="待发货" value="READY_TO_SHIP" />
          <el-option label="处理中" value="WORKING" />
          <el-option label="已发货" value="SHIPPED" />
          <el-option label="接收中" value="RECEIVING" />
          <el-option label="已关闭" value="CLOSED" />
          <el-option label="已删除" value="DELETED" />
          <el-option label="已取消" value="CANCELLED" />
        </el-select>
        <el-button type="primary" @click="handleSearch" :loading="searchLoading">
          <el-icon><Search /></el-icon> 搜索
        </el-button>
        <el-button plain @click="resetSearch">
          <el-icon><Refresh /></el-icon> 重置
        </el-button>
      </div>
    </div>

    <!-- 数据表格 -->
    <div class="table-card">
      <el-table
        :data="shipments"
        v-loading="loading"
        style="width: 100%"
        height="calc(100vh - 296px)"
        :default-sort="{ prop: 'plan_created_at', order: 'descending' }"
        row-class-name="shipment-row"
        :header-cell-style="{background:'#f8f9fa',color:'#555',fontWeight:600}"
        :cell-style="{padding:'10px 0'}"
      >
        <el-table-column label="店铺名称" width="120" show-overflow-tooltip fixed="left">
          <template #default>
            <el-tag size="small" effect="plain" type="info">{{ getShopName(selectedShopId) || '-' }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="inbound_plan_id" label="入库计划ID" width="220" fixed="left">
          <template #default="scope">
            <div style="display:flex;align-items:center;gap:4px;">
              <el-tooltip :content="scope.row.inbound_plan_id" placement="top" :disabled="!scope.row.inbound_plan_id || scope.row.inbound_plan_id.length <= 24">
                <span style="font-family:monospace;font-size:13px;color:#333;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;">{{ scope.row.inbound_plan_id }}</span>
              </el-tooltip>
              <el-tooltip content="复制" placement="top">
                <el-icon style="cursor:pointer;color:#909399;flex-shrink:0;" @click="copyText(scope.row.inbound_plan_id)"><DocumentCopy /></el-icon>
              </el-tooltip>
              <el-tooltip content="同步该入库计划" placement="top">
                <el-icon
                  style="cursor:pointer;color:#409eff;flex-shrink:0;"
                  :class="{ 'plan-sync-loading': syncingPlanId === scope.row.inbound_plan_id }"
                  @click="syncInboundPlanById(scope.row.inbound_plan_id)"
                >
                  <RefreshRight />
                </el-icon>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="shipment_name" label="货件名称" min-width="220" show-overflow-tooltip>
          <template #default="scope">
            <span style="font-weight:500;color:#1a1a2e;">{{ scope.row.shipment_name }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="shipment_confirmation_id" label="货件编号" width="120">
          <template #default="scope">
            <span style="font-family:monospace;font-size:12px;color:#888;">{{ scope.row.shipment_confirmation_id }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="amazon_reference_id" label="亚马逊参考号" width="120">
          <template #default="scope">
            <span style="font-family:monospace;font-size:12px;color:#888;">{{ scope.row.amazon_reference_id }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="destination_warehouse_id" label="目标仓库" width="100" align="center">
          <template #default="scope">
            <el-tag v-if="scope.row.destination_warehouse_id" size="small" effect="plain">{{ scope.row.destination_warehouse_id }}</el-tag>
            <span v-else style="color:#bbb;">-</span>
          </template>
        </el-table-column>

        <el-table-column prop="box_count" label="箱数" width="80" align="center">
          <template #default="scope">
            <span style="font-weight:600;color:#409eff;">{{ scope.row.box_count ?? '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="shipment_status" label="状态" width="100" align="center">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.shipment_status)" size="small" effect="dark" round>
              {{ getStatusText(scope.row.shipment_status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="SKU明细" min-width="300">
          <template #default="scope">
            <div class="sku-items" v-if="scope.row.skus && scope.row.skus.length > 0">
              <div v-for="(item, idx) in scope.row.skus" :key="idx" class="sku-item">
                <span class="sku-code">{{ item.sku }}</span>
                <span v-if="item.name_cn" class="sku-name"> - {{ item.name_cn }}</span>
                <span class="sku-qty"> * {{ item.quantity }}</span>
              </div>
            </div>
            <span v-else style="color:#ccc;">—</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template #default="scope">
            <el-tooltip content="货件详情" placement="top">
              <el-button type="primary" link @click="viewShipmentDetail(scope.row)">
                <el-icon><InfoFilled /></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip content="查看箱子" placement="top">
              <el-button type="info" link @click="viewShipmentBoxes(scope.row)">
                <el-icon><Box /></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip content="打印箱唛" placement="top">
              <el-button type="success" link :loading="labelsLoading" @click="printShipmentLabels(scope.row)">
                <el-icon><Printer /></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip content="导出发票" placement="top">
              <el-button type="warning" link :loading="invoiceExportLoading" @click="exportInvoice(scope.row)">
                <el-icon><Download /></el-icon>
              </el-button>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrap">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.page_size"
          :page-sizes="[20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          :hide-on-single-page="false"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>

    <!-- 货件详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      width="70%"
      :destroy-on-close="true"
      align-center
    >
      <template #header>
        <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
          <span>{{ `货件详情 - ${currentShipment?.shipment_id}` }}</span>
          <el-button type="warning" size="small" :loading="detailSyncLoading" @click="syncDetail">
            <el-icon><RefreshRight /></el-icon> 同步详情
          </el-button>
        </div>
      </template>
      <div v-loading="detailLoading">
        <div v-if="shipmentDetailData" class="shipment-detail">
          <!-- 基础信息 -->
          <div class="detail-section">
            <h4>基础信息</h4>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="货件ID">{{ shipmentDetailData.shipment_id }}</el-descriptions-item>
              <el-descriptions-item label="入库计划ID">{{ shipmentDetailData.inbound_plan_id }}</el-descriptions-item>
              <el-descriptions-item label="货件名称">{{ shipmentDetailData.name }}</el-descriptions-item>
              <el-descriptions-item label="货件编号">{{ shipmentDetailData.shipment_confirmation_id || '-' }}</el-descriptions-item>
              <el-descriptions-item label="亚马逊参考号">{{ shipmentDetailData.amazon_reference_id || '-' }}</el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag :type="getStatusType(shipmentDetailData.status)" size="small" effect="dark" round>
                  {{ getStatusText(shipmentDetailData.status) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="目标仓库">{{ shipmentDetailData.destination_warehouse_id || '-' }}</el-descriptions-item>
              <el-descriptions-item label="目标类型">{{ shipmentDetailData.destination_type || '-' }}</el-descriptions-item>
              <el-descriptions-item label="来源类型">{{ shipmentDetailData.source_type || '-' }}</el-descriptions-item>
              <el-descriptions-item label="同步时间">{{ formatDate(shipmentDetailData.sync_time) }}</el-descriptions-item>
              <el-descriptions-item label="Placement Option ID">{{ shipmentDetailData.placement_option_id || '-' }}</el-descriptions-item>
              <el-descriptions-item label="Transportation Option ID">{{ shipmentDetailData.selected_transportation_option_id || '-' }}</el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 目的地址 -->
          <div class="detail-section" v-if="parsedDestinationAddress">
            <h4>目的地址</h4>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="名称">{{ parsedDestinationAddress.name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="公司">{{ parsedDestinationAddress.companyName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="地址行1">{{ parsedDestinationAddress.addressLine1 || '-' }}</el-descriptions-item>
              <el-descriptions-item label="地址行2">{{ parsedDestinationAddress.addressLine2 || '-' }}</el-descriptions-item>
              <el-descriptions-item label="城市">{{ parsedDestinationAddress.city || '-' }}</el-descriptions-item>
              <el-descriptions-item label="州/省">{{ parsedDestinationAddress.stateOrProvinceCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="邮编">{{ parsedDestinationAddress.postalCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="国家">{{ parsedDestinationAddress.countryCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="电话">{{ parsedDestinationAddress.phoneNumber || '-' }}</el-descriptions-item>
              <el-descriptions-item label="区县">{{ parsedDestinationAddress.districtOrCounty || '-' }}</el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 发货地址 -->
          <div class="detail-section" v-if="parsedSourceAddress">
            <h4>发货地址</h4>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="名称">{{ parsedSourceAddress.name || '-' }}</el-descriptions-item>
              <el-descriptions-item label="公司">{{ parsedSourceAddress.companyName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="地址行1">{{ parsedSourceAddress.addressLine1 || '-' }}</el-descriptions-item>
              <el-descriptions-item label="地址行2">{{ parsedSourceAddress.addressLine2 || '-' }}</el-descriptions-item>
              <el-descriptions-item label="城市">{{ parsedSourceAddress.city || '-' }}</el-descriptions-item>
              <el-descriptions-item label="州/省">{{ parsedSourceAddress.stateOrProvinceCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="邮编">{{ parsedSourceAddress.postalCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="国家">{{ parsedSourceAddress.countryCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="电话">{{ parsedSourceAddress.phoneNumber || '-' }}</el-descriptions-item>
              <el-descriptions-item label="区县">{{ parsedSourceAddress.districtOrCounty || '-' }}</el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 配送窗口 -->
          <div class="detail-section" v-if="parsedDeliveryWindow">
            <h4>配送窗口</h4>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="窗口ID">{{ parsedDeliveryWindow.deliveryWindowOptionId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="可用性">{{ parsedDeliveryWindow.availabilityType || '-' }}</el-descriptions-item>
              <el-descriptions-item label="开始时间">{{ formatDate(parsedDeliveryWindow.startDate) }}</el-descriptions-item>
              <el-descriptions-item label="结束时间">{{ formatDate(parsedDeliveryWindow.endDate) }}</el-descriptions-item>
              <el-descriptions-item label="可编辑截止">{{ formatDate(parsedDeliveryWindow.editableUntil) }}</el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 追踪详情 -->
          <div class="detail-section" v-if="parsedTrackingDetails">
            <h4>追踪详情</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="LTL 追踪">
                <pre v-if="hasLtlTracking" class="json-pre">{{ JSON.stringify(parsedTrackingDetails.ltlTrackingDetail, null, 2) }}</pre>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="SPD 追踪">
                <pre v-if="hasSpdTracking" class="json-pre">{{ JSON.stringify(parsedTrackingDetails.spdTrackingDetail, null, 2) }}</pre>
                <span v-else>-</span>
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 日期信息 -->
          <div class="detail-section" v-if="parsedDates && Object.keys(parsedDates).length > 0">
            <h4>日期信息</h4>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="Dates JSON">
                <pre class="json-pre">{{ JSON.stringify(parsedDates, null, 2) }}</pre>
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </div>
        <el-empty v-else description="暂无详情数据" />
      </div>
    </el-dialog>

    <!-- 箱子列表对话框 -->
    <el-dialog
      v-model="boxesDialogVisible"
      :title="`箱子列表 - ${selectedBoxesShipment?.shipment_confirmation_id || selectedBoxesShipment?.shipment_id}`"
      width="85%"
      :destroy-on-close="true"
      @closed="allBoxesExpanded = false"
      align-center
    >
      <div v-loading="boxesLoading">
        <div class="boxes-dialog-header">
          <el-button type="warning" size="small" :loading="boxesSyncLoading" @click="syncInboundPlanBoxes">
            <el-icon><RefreshRight /></el-icon>
            同步最新数据
          </el-button>
          <el-button
            v-if="shipmentBoxes && shipmentBoxes.length > 0"
            type="primary"
            size="small"
            @click="toggleAllBoxesExpansion"
          >
            {{ allBoxesExpanded ? '全部收缩' : '全部展开' }}
          </el-button>
        </div>
        <div v-if="shipmentBoxes && shipmentBoxes.length > 0">
          <el-table ref="boxesTable" :data="shipmentBoxes" stripe style="width: 100%" border>
            <el-table-column type="expand" width="45">
              <template #default="scope">
                <div class="box-expand-content">
                  <el-descriptions :column="2" border size="small" class="box-detail-desc">
                    <el-descriptions-item label="inbound_plan_id">{{ scope.row.inbound_plan_id || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="package_id">{{ scope.row.package_id || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="content_information_source">{{ scope.row.content_information_source || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="template_name">{{ scope.row.template_name || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="external_container_identifier">{{ scope.row.external_container_identifier || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="external_container_identifier_type">{{ scope.row.external_container_identifier_type || '-' }}</el-descriptions-item>
                    <el-descriptions-item label="同步时间">{{ formatDate(scope.row.sync_time) }}</el-descriptions-item>
                  </el-descriptions>
                  <div class="box-items-section" v-if="getBoxItems(scope.row).length > 0">
                    <h5>箱内商品</h5>
                    <el-table :data="getBoxItems(scope.row)" size="small" border>
                      <el-table-column prop="msku" label="MSKU" min-width="140" />
                      <el-table-column prop="sku_name_cn" label="产品名称" min-width="140" show-overflow-tooltip />
                      <el-table-column prop="labelOwner" label="标签所有者" min-width="110" />
                      <el-table-column prop="prepOwner" label="预处理所有者" min-width="110" />
                      <el-table-column prop="quantity" label="数量" min-width="70" align="center" />
                      <el-table-column prop="expiration" label="有效期" min-width="110" />
                      <el-table-column prop="manufacturingLotCode" label="生产批号" min-width="110" />
                    </el-table>
                  </div>
                  <el-empty v-else description="暂无箱内商品数据" :image-size="60" />
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="box_id" label="箱子ID" min-width="1" show-overflow-tooltip />
            <el-table-column prop="quantity" label="数量" min-width="1" align="center" />
            <el-table-column label="尺寸" min-width="1" align="center">
              <template #default="scope">
                <span v-if="scope.row.dimensions_length || scope.row.dimensions_width || scope.row.dimensions_height">
                  {{ scope.row.dimensions_length }}×{{ scope.row.dimensions_width }}×{{ scope.row.dimensions_height }} {{ scope.row.dimensions_unit }}
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="重量" min-width="1" align="center">
              <template #default="scope">
                <span v-if="scope.row.weight_value">
                  {{ scope.row.weight_value }} {{ scope.row.weight_unit }}
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="sync_time" label="同步时间" min-width="1" align="center">
              <template #default="scope">
                {{ formatDate(scope.row.sync_time) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" min-width="1" align="center">
              <template #default="scope">
                <el-button type="success" link size="small" :loading="labelsLoading" @click="printShipmentLabels(selectedBoxesShipment, scope.row.box_id)">
                  打印箱唛
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 箱子分页 -->
          <div class="pagination-container">
            <el-pagination
              v-model:current-page="boxesPagination.page"
              v-model:page-size="boxesPagination.page_size"
              :page-sizes="[20, 50, 100]"
              :total="boxesPagination.total"
              layout="total, sizes, prev, pager, next, jumper"
              :hide-on-single-page="false"
              @current-change="handleBoxesPageChange"
              @size-change="handleBoxesSizeChange"
            />
          </div>
        </div>
        <el-empty v-else description="暂无箱子数据" />
      </div>
    </el-dialog>

    <!-- 导出发票货代选择对话框 -->
    <el-dialog
      v-model="invoiceDialogVisible"
      title="选择货代"
      width="420px"
      :destroy-on-close="true"
      align-center
    >
      <el-form label-width="80px">
        <el-form-item label="货代" required>
          <el-select
            v-model="invoiceExportProviderId"
            placeholder="请选择货代"
            clearable
            style="width:100%"
          >
            <el-option
              v-for="provider in logisticsProviders"
              :key="provider.id"
              :label="provider.name"
              :value="provider.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="invoiceDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmExportInvoice">确定导出</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useListQuerySync } from '@/composables/useListQuerySync.js'
import { ElMessage } from 'element-plus'
import { Search, Refresh, RefreshRight, InfoFilled, Box, Printer, Download, Van, DocumentCopy } from '@element-plus/icons-vue'
import {
  getInboundShipments,
  getInboundShipmentDetail,
  syncInboundShipments,
  syncInboundShipmentDetail,
  syncInboundPlan,
  getAmazonShipmentLabels,
  getWarehouseOptions,
  getAmazonInboundPlanBoxes,
  syncAmazonInboundPlanBoxes,
  exportAmazonShipmentInvoice,
  getLogisticsProviderOptions
} from '@/services/api.js'
import { useShopCache } from '@/composables/useShopCache'

export default {
  name: 'AmazonShipmentView',
  components: {
    Search,
    Refresh,
    RefreshRight,
    InfoFilled,
    Box,
    Printer,
    Download,
    Van,
    DocumentCopy
  },
  setup() {
    const loading = ref(false)
    const searchLoading = ref(false)
    const refreshLoading = ref(false)
    const syncLoading = ref(false)
    const syncingPlanId = ref('')
    const labelsLoading = ref(false)
    const shipments = ref([])
    const warehouses = ref([])
    const logisticsProviders = ref([])

    const { shopList, fetchShopList, refreshShopList, getShopName, defaultShopId } = useShopCache()
    const selectedShopId = ref(null)

    // 搜索表单
    const searchForm = reactive({
      inbound_plan_id: '',
      shipment_confirmation_id: '',
      shipment_name: '',
      amazon_reference_id: '',
      destination_warehouse_id: '',
      status: 'WORKING'
    })

    // 分页状态
    const pagination = reactive({
      page: 1,
      page_size: 20,
      total: 0
    })

    const { initFromQuery, syncQuery, watchQuery } = useListQuerySync({
      page: { get: () => pagination.page, set: v => pagination.page = v, type: 'number', default: 1 },
      page_size: { get: () => pagination.page_size, set: v => pagination.page_size = v, type: 'number', default: 20 },
      inbound_plan_id: { get: () => searchForm.inbound_plan_id, set: v => searchForm.inbound_plan_id = v },
      shipment_confirmation_id: { get: () => searchForm.shipment_confirmation_id, set: v => searchForm.shipment_confirmation_id = v },
      shipment_name: { get: () => searchForm.shipment_name, set: v => searchForm.shipment_name = v },
      amazon_reference_id: { get: () => searchForm.amazon_reference_id, set: v => searchForm.amazon_reference_id = v },
      destination_warehouse_id: { get: () => searchForm.destination_warehouse_id, set: v => searchForm.destination_warehouse_id = v },
      status: { get: () => searchForm.status, set: v => searchForm.status = v }
    })

    // 详情对话框
    const detailDialogVisible = ref(false)
    const detailLoading = ref(false)
    const detailSyncLoading = ref(false)
    const currentShipment = ref(null)
    const shipmentDetailData = ref(null)

    // 箱子列表对话框
    const boxesDialogVisible = ref(false)
    const boxesLoading = ref(false)
    const selectedBoxesShipment = ref(null)
    const shipmentBoxes = ref([])

    // 箱子分页
    const boxesPagination = reactive({
      page: 1,
      page_size: 20,
      total: 0
    })

    // 获取仓库列表
    const fetchWarehouses = async () => {
      if (!selectedShopId.value) return
      try {
        const response = await getWarehouseOptions(selectedShopId.value)
        if (response.data.status === 'success') {
          warehouses.value = response.data.data || []
        }
      } catch (error) {
        console.error('获取仓库列表失败:', error)
      }
    }

    const fetchLogisticsProviders = async () => {
      try {
        const response = await getLogisticsProviderOptions()
        if (response.data.status === 'success') {
          logisticsProviders.value = response.data.data || []
        }
      } catch (error) {
        console.error('获取货代列表失败:', error)
      }
    }

    // 获取货件列表
    const fetchShipments = async () => {
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }

      syncQuery()
      loading.value = true
      try {
        const params = {
          shop_id: selectedShopId.value,
          page: pagination.page,
          page_size: pagination.page_size
        }

        if (searchForm.inbound_plan_id) {
          params.inbound_plan_id = searchForm.inbound_plan_id
        }
        if (searchForm.shipment_confirmation_id) {
          params.shipment_confirmation_id = searchForm.shipment_confirmation_id
        }
        if (searchForm.shipment_name) {
          params.shipment_name = searchForm.shipment_name
        }
        if (searchForm.amazon_reference_id) {
          params.amazon_reference_id = searchForm.amazon_reference_id
        }
        if (searchForm.destination_warehouse_id) {
          params.destination_warehouse_id = searchForm.destination_warehouse_id
        }
        if (searchForm.status) {
          params.status = searchForm.status
        }

        const response = await getInboundShipments(params)

        if (response.data.status === 'success') {
          const data = response.data.data || {}
          shipments.value = data.list || []
          pagination.total = data.total || 0
          pagination.page = data.page || 1
          pagination.page_size = data.page_size || 20
        } else {
          ElMessage.error(response.data.message || '获取货件列表失败')
          shipments.value = []
          pagination.total = 0
        }
      } catch (error) {
        console.error('获取货件列表失败:', error)
        ElMessage.error('获取货件列表失败: ' + (error.response?.data?.message || error.message))
        shipments.value = []
        pagination.total = 0
      } finally {
        loading.value = false
      }
    }

    // 搜索
    const handleSearch = async () => {
      searchLoading.value = true
      pagination.page = 1
      await fetchShipments()
      searchLoading.value = false
    }

    // 重置搜索
    const resetSearch = async () => {
      searchLoading.value = true
      searchForm.inbound_plan_id = ''
      searchForm.shipment_confirmation_id = ''
      searchForm.shipment_name = ''
      searchForm.amazon_reference_id = ''
      searchForm.destination_warehouse_id = ''
      searchForm.status = 'WORKING'
      pagination.page = 1
      pagination.page_size = 20
      await fetchShipments()
      searchLoading.value = false
    }

    // 刷新数据
    const refreshData = async () => {
      refreshLoading.value = true
      await fetchShipments()
      refreshLoading.value = false
    }

    // 切换店铺
    const handleShopChange = async (val) => {
      if (val === '__refresh__') {
        await refreshShopList()
        selectedShopId.value = defaultShopId()
        pagination.page = 1
        fetchWarehouses()
        fetchShipments()
        return
      }
      pagination.page = 1
      fetchWarehouses()
      fetchShipments()
    }

    // 同步最新货件
    const syncAllData = async () => {
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }

      syncLoading.value = true
      try {
        const response = await syncInboundShipments({ shop_id: selectedShopId.value })
        if (response.data.status === 'success') {
          ElMessage.success(response.data.message || '同步完成')
          await fetchShipments()
        } else {
          ElMessage.error(response.data.message || '同步失败')
        }
      } catch (error) {
        console.error('同步数据失败:', error)
        ElMessage.error('同步数据失败: ' + (error.response?.data?.message || error.message))
      } finally {
        syncLoading.value = false
      }
    }

    // 同步单个入库计划
    const syncInboundPlanById = async (planId) => {
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }
      if (!planId) {
        ElMessage.warning('缺少入库计划ID')
        return
      }
      if (syncingPlanId.value === planId) return

      syncingPlanId.value = planId
      try {
        const response = await syncInboundPlan(planId, { shop_id: selectedShopId.value })
        if (response.data.status === 'success') {
          ElMessage.success(response.data.message || '同步完成')
          await fetchShipments()
        } else {
          ElMessage.error(response.data.message || '同步失败')
        }
      } catch (error) {
        console.error('同步入库计划失败:', error)
        ElMessage.error('同步入库计划失败: ' + (error.response?.data?.message || error.message))
      } finally {
        syncingPlanId.value = ''
      }
    }

    // 分页变化
    const handlePageChange = (page) => {
      pagination.page = page
      fetchShipments()
    }

    // 每页数量变化
    const handleSizeChange = (size) => {
      pagination.page_size = size
      pagination.page = 1
      fetchShipments()
    }

    // 查看货件详情
    const viewShipmentDetail = async (shipment) => {
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }

      currentShipment.value = shipment
      detailDialogVisible.value = true
      detailLoading.value = true
      shipmentDetailData.value = null

      try {
        const response = await getInboundShipmentDetail(shipment.shipment_id, selectedShopId.value)
        if (response.data.status === 'success') {
          shipmentDetailData.value = response.data.data || null
        } else {
          ElMessage.error(response.data.message || '获取货件详情失败')
        }
      } catch (error) {
        console.error('获取货件详情失败:', error)
        ElMessage.error('获取货件详情失败: ' + (error.response?.data?.message || error.message))
      } finally {
        detailLoading.value = false
      }
    }

    // 同步单个货件详情
    const syncDetail = async () => {
      if (!currentShipment.value || !selectedShopId.value) return
      detailSyncLoading.value = true
      try {
        const response = await syncInboundShipmentDetail(currentShipment.value.shipment_id, selectedShopId.value)
        if (response.data.status === 'success') {
          ElMessage.success('同步成功')
          await viewShipmentDetail(currentShipment.value)
        } else {
          ElMessage.error(response.data.message || '同步失败')
        }
      } catch (error) {
        console.error('同步货件详情失败:', error)
        ElMessage.error('同步失败: ' + (error.response?.data?.message || error.message))
      } finally {
        detailSyncLoading.value = false
      }
    }

    // 查看箱子列表
    const viewShipmentBoxes = async (shipment) => {
      selectedBoxesShipment.value = shipment
      boxesDialogVisible.value = true
      boxesLoading.value = true
      boxesPagination.page = 1
      boxesPagination.page_size = 20

      await loadShipmentBoxes()
    }

    // 加载货件箱子
    const loadShipmentBoxes = async () => {
      if (!selectedBoxesShipment.value) return
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }

      boxesLoading.value = true
      try {
        const response = await getAmazonInboundPlanBoxes(
          selectedBoxesShipment.value.shipment_confirmation_id,
          selectedShopId.value,
          {
            page: boxesPagination.page,
            page_size: boxesPagination.page_size
          }
        )

        if (response.data.status === 'success') {
          const data = response.data.data || {}
          shipmentBoxes.value = data.list || []
          boxesPagination.total = data.total || 0
          boxesPagination.page = data.page || 1
          boxesPagination.page_size = data.page_size || 20
        } else {
          shipmentBoxes.value = []
          boxesPagination.total = 0
          ElMessage.error(response.data.message || '获取箱子列表失败')
        }
      } catch (error) {
        console.error('获取箱子列表失败:', error)
        ElMessage.error('获取箱子列表失败: ' + (error.response?.data?.message || error.message))
        shipmentBoxes.value = []
        boxesPagination.total = 0
      } finally {
        boxesLoading.value = false
      }
    }

    // 箱子分页变化
    const handleBoxesPageChange = (page) => {
      boxesPagination.page = page
      loadShipmentBoxes()
    }

    // 箱子每页数量变化
    const handleBoxesSizeChange = (size) => {
      boxesPagination.page_size = size
      boxesPagination.page = 1
      loadShipmentBoxes()
    }

    // 同步入库计划箱子数据
    const boxesSyncLoading = ref(false)
    const syncInboundPlanBoxes = async () => {
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }
      const planId = shipmentBoxes.value[0]?.inbound_plan_id
        || selectedBoxesShipment.value?.inbound_plan_id
      if (!planId) {
        ElMessage.error('未获取到入库计划ID，无法同步')
        return
      }
      boxesSyncLoading.value = true
      try {
        const response = await syncAmazonInboundPlanBoxes(planId, { shop_id: selectedShopId.value })
        if (response.data.status === 'success') {
          ElMessage.success(response.data.message || '箱子数据同步完成')
          await loadShipmentBoxes()
        } else {
          ElMessage.error(response.data.message || '箱子数据同步失败')
        }
      } catch (error) {
        console.error('同步箱子数据失败:', error)
        ElMessage.error('同步箱子数据失败: ' + (error.response?.data?.message || error.message))
      } finally {
        boxesSyncLoading.value = false
      }
    }

    // 全部展开/收缩箱子
    const allBoxesExpanded = ref(false)
    const boxesTable = ref(null)
    const toggleAllBoxesExpansion = () => {
      allBoxesExpanded.value = !allBoxesExpanded.value
      shipmentBoxes.value.forEach((row) => {
        boxesTable.value.toggleRowExpansion(row, allBoxesExpanded.value)
      })
    }

    // 获取箱子商品列表（优先用 items 字段，兼容 items_json）
    const getBoxItems = (row) => {
      if (row.items && Array.isArray(row.items)) {
        return row.items
      }
      try {
        return JSON.parse(row.items_json || '[]')
      } catch {
        return []
      }
    }

    // 打印箱唛标签
    const printShipmentLabels = async (shipment, boxId = null) => {
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }
      labelsLoading.value = true
      try {
        const response = await getAmazonShipmentLabels(shipment.shipment_confirmation_id, selectedShopId.value, 'PackageLabel_Thermal_NonPCP', 2, boxId)
        const downloadUrl = response.data?.data?.payload?.DownloadURL
        if (!downloadUrl) {
          ElMessage.error('未获取到箱唛下载链接')
          return
        }
        window.open(downloadUrl, '_blank')
        ElMessage.success('箱唛已打开，请在浏览器新标签页中查看或下载')
      } catch (error) {
        console.error('下载箱唛失败:', error)
        ElMessage.error('下载箱唛失败: ' + (error.message || '未知错误'))
      } finally {
        labelsLoading.value = false
      }
    }

    // 导出发票
    const invoiceExportShipment = ref(null)
    const invoiceExportProviderId = ref(null)
    const invoiceDialogVisible = ref(false)
    const invoiceExportLoading = ref(false)

    const exportInvoice = (shipment) => {
      if (!selectedShopId.value) {
        ElMessage.warning('请选择店铺')
        return
      }
      invoiceExportShipment.value = shipment
      invoiceExportProviderId.value = null
      invoiceDialogVisible.value = true
    }

    const confirmExportInvoice = async () => {
      if (!invoiceExportProviderId.value) {
        ElMessage.warning('请选择货代')
        return
      }
      const shipment = invoiceExportShipment.value
      if (!shipment) return
      invoiceDialogVisible.value = false
      invoiceExportLoading.value = true
      try {
        const response = await exportAmazonShipmentInvoice(shipment.shipment_confirmation_id, selectedShopId.value, invoiceExportProviderId.value)
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${shipment.shipment_confirmation_id}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        ElMessage.success('发票导出成功')
      } catch (error) {
        console.error('导出发票失败:', error)
        ElMessage.error('导出发票失败')
      } finally {
        invoiceExportLoading.value = false
      }
    }

    // 解析 JSON
    const safeParseJson = (str) => {
      try {
        return JSON.parse(str || '{}')
      } catch {
        return {}
      }
    }

    // 计算属性：解析详情中的 JSON 字段
    const parsedDestinationAddress = computed(() => {
      if (!shipmentDetailData.value?.destination_address_json) return null
      return safeParseJson(shipmentDetailData.value.destination_address_json)
    })

    const parsedSourceAddress = computed(() => {
      if (!shipmentDetailData.value?.source_address_json) return null
      return safeParseJson(shipmentDetailData.value.source_address_json)
    })

    const parsedDeliveryWindow = computed(() => {
      if (!shipmentDetailData.value?.selected_delivery_window_json) return null
      return safeParseJson(shipmentDetailData.value.selected_delivery_window_json)
    })

    const parsedTrackingDetails = computed(() => {
      if (!shipmentDetailData.value?.tracking_details_json) return null
      return safeParseJson(shipmentDetailData.value.tracking_details_json)
    })

    const hasLtlTracking = computed(() => {
      const td = parsedTrackingDetails.value
      return td && td.ltlTrackingDetail && Object.keys(td.ltlTrackingDetail).length > 0
    })

    const hasSpdTracking = computed(() => {
      const td = parsedTrackingDetails.value
      return td && td.spdTrackingDetail && Object.keys(td.spdTrackingDetail).length > 0
    })

    const parsedDates = computed(() => {
      if (!shipmentDetailData.value?.dates_json) return null
      return safeParseJson(shipmentDetailData.value.dates_json)
    })

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return dateString.replace('T', ' ')
    }

    // 状态样式
    const getStatusType = (status) => {
      const typeMap = {
        'READY_TO_SHIP': 'primary',
        'WORKING': 'warning',
        'SHIPPED': 'info',
        'RECEIVING': 'success',
        'CLOSED': 'success',
        'DELETED': 'danger',
        'CANCELLED': 'danger'
      }
      return typeMap[status] || 'info'
    }

    const getStatusText = (status) => {
      const textMap = {
        'READY_TO_SHIP': '待发货',
        'WORKING': '处理中',
        'SHIPPED': '已发货',
        'RECEIVING': '接收中',
        'CLOSED': '已关闭',
        'DELETED': '已删除',
        'CANCELLED': '已取消'
      }
      return textMap[status] || status
    }

    // 复制文本到剪贴板
    const copyText = async (text) => {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success('已复制到剪贴板')
      } catch (err) {
        // 降级方案
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        ElMessage.success('已复制到剪贴板')
      }
    }

    watchQuery(() => fetchShipments())

    onMounted(async () => {
      await fetchShopList()
      fetchLogisticsProviders()
      if (shopList.value.length > 0) {
        selectedShopId.value = defaultShopId()
      }
      initFromQuery()
      fetchWarehouses()
      fetchShipments()
    })

    return {
      loading,
      shipments,
      warehouses,
      searchForm,
      pagination,
      detailDialogVisible,
      detailLoading,
      detailSyncLoading,
      currentShipment,
      shipmentDetailData,
      syncLoading,
      syncingPlanId,
      searchLoading,
      refreshLoading,
      labelsLoading,
      shopList,
      selectedShopId,
      logisticsProviders,
      fetchShipments,
      handleSearch,
      resetSearch,
      refreshData,
      syncAllData,
      syncInboundPlanById,
      handlePageChange,
      handleSizeChange,
      handleShopChange,
      viewShipmentDetail,
      syncDetail,
      viewShipmentBoxes,
      printShipmentLabels,
      exportInvoice,
      invoiceDialogVisible,
      invoiceExportProviderId,
      invoiceExportLoading,
      confirmExportInvoice,
      formatDate,
      getStatusType,
      getStatusText,
      getShopName,
      copyText,
      parsedDestinationAddress,
      parsedSourceAddress,
      parsedDeliveryWindow,
      parsedTrackingDetails,
      hasLtlTracking,
      hasSpdTracking,
      parsedDates,
      // 箱子列表
      boxesDialogVisible,
      boxesLoading,
      selectedBoxesShipment,
      shipmentBoxes,
      boxesPagination,
      loadShipmentBoxes,
      handleBoxesPageChange,
      handleBoxesSizeChange,
      getBoxItems,
      allBoxesExpanded,
      toggleAllBoxesExpansion,
      boxesTable,
      boxesSyncLoading,
      syncInboundPlanBoxes
    }
  }
}
</script>

<style scoped>
.amazon-shipment-page {
  max-width: 1600px;
  margin: 0 auto;
  padding: 24px 24px 40px;
}

/* ===== 头部 ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}
.page-title {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px;
  letter-spacing: -0.5px;
}
.page-subtitle {
  font-size: 14px;
  color: #888;
  margin: 0;
}
.header-actions {
  display: flex;
  gap: 10px;
}

/* ===== 筛选栏 ===== */
.filter-bar {
  background: #fff;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* ===== 表格卡片 ===== */
.table-card {
  background: #fff;
  border-radius: 14px;
  padding: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03);
  overflow: hidden;
}
:deep(.el-table) { --el-table-border-color: #f0f0f0; }
:deep(.el-scrollbar__wrap) {
  overflow-x: auto !important;
}
:deep(.el-table__body-wrapper) {
  overflow-x: auto !important;
}
:deep(.el-table__body-wrapper::-webkit-scrollbar) {
  height: 8px;
}
:deep(.el-table__body-wrapper::-webkit-scrollbar-thumb) {
  background: #c0c4cc;
  border-radius: 4px;
}
:deep(.el-table__body-wrapper::-webkit-scrollbar-track) {
  background: transparent;
  border-radius: 4px;
}
:deep(.shipment-row:hover) { background-color: #fafbff !important; }

.plan-sync-loading {
  animation: plan-rotating 1s linear infinite;
}
@keyframes plan-rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.sku-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 0;
}
.sku-item {
  font-size: 12px;
  line-height: 1.5;
}
.sku-code {
  color: #1a1a2e;
  font-family: monospace;
  font-weight: 500;
}
.sku-name {
  color: #666;
}
.sku-qty {
  color: #909399;
  font-family: monospace;
}

/* 分页 */
.pagination-wrap {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
}

.shipment-detail .detail-section {
  margin-top: 20px;
}
.shipment-detail .detail-section:first-child {
  margin-top: 0;
}
.shipment-detail .detail-section h4 {
  margin-bottom: 12px;
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 600;
}

.json-pre {
  margin: 0;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.boxes-dialog-header {
  margin-bottom: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 箱子展开详情 */
.box-expand-content {
  padding: 12px 24px;
  background-color: #fafafa;
  border-radius: 4px;
}

.box-detail-desc {
  margin-bottom: 12px;
}

.box-items-section {
  margin-top: 12px;
}

.box-items-section h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #1a1a2e;
  font-weight: 600;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .amazon-shipment-page {
    padding: 16px 16px 40px;
  }
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .filter-group {
    justify-content: stretch;
  }
  .pagination-container {
    text-align: center;
  }
  .shipment-detail .detail-section h4 {
    font-size: 14px;
  }
  .json-pre {
    font-size: 11px;
    max-height: 200px;
  }
  .box-expand-content {
    padding: 10px 12px;
  }
}
</style>
